import React from 'react'
import { LoadingBoundary } from 'components/patterns/suspense/Suspense'
import { DataWrapper, DefaultObject, Pending } from 'components/patterns/suspense/internals/types'
import { ID } from 'lib'

/**
 * Functional component who provides a pending state
 */
export type PendingFC<T, R = DefaultObject> = (props: Pending<T, R>) => JSX.Element
/**
 * When a component requires both static and async props, this type allows them to be added
 */
type OptionalStaticProps<R> = R
/**
 * How all async data is passed to the component
 */
type ArrayedDataProps<T> = { data: DataWrapper<Array<T>> }

/**
 * How singular async data works
 */
type SingleDataProps<T> = { data: DataWrapper<T> }

/**
 * How singular async data works
 */
type AllSingleDataProps<T, R> = SingleDataProps<T> & R

/**
 * The component as a prop
 */
type ComponentProps<T, R> = {
	Component: PendingFC<T, R>
}

/**
 * The component and static props required to generate a list + a list of dynamic data
 */
type AsyncListProps<T, R> = ComponentProps<T, R> & ArrayedDataProps<T> & OptionalStaticProps<R>

/**
 * The fields required to unwrap a component
 */
type AsyncComponentProps<T, R> = ComponentProps<T, R> & SingleDataProps<T>

export const generatePendingList = <T, R>(Component: PendingFC<T, R>, count: number): JSX.Element => {
	const array = new Array(count).fill(Math.random)
	return (
		<>
			{array.map((x, i) => (
				<Component key={i} pending={true} />
			))}
		</>
	)
}

const calcKey = <T,>(item: T) => {
	try {
		if (typeof item === 'object' && item) {
			const entry = Object.entries(item).find(([key]) => key === 'id' || key === '_id' || key === 'key')
			if (entry && typeof entry[1] === 'string') return entry[1]
		}
	} catch (err) {}
	return ID()
}

const PendingListView = <T, R>({ data, Component, ...props }: AsyncListProps<T, R>): JSX.Element => {
	const array = data.read()
	return (
		<>
			{array.map(item => (
				<Component key={calcKey(item)} {...item} {...props} pending={undefined} />
			))}
		</>
	)
}
type ListViewProps<T, R> = ComponentProps<T, R> & {
	fallbackCount?: number
} & R
type ListViewPropsWithData<T, R> = AsyncListProps<T, R> & {
	fallbackCount?: number
}

export const PendingList = <T, R>(props: ListViewPropsWithData<T, R>): JSX.Element => (
	<LoadingBoundary fallback={generatePendingList(props.Component, props.fallbackCount || 3)}>
		<PendingListView {...props} />
	</LoadingBoundary>
)
export const createPendingList =
	<T, R = DefaultObject>(listProps: ListViewProps<T, R>) =>
	({ data }: ArrayedDataProps<T>): JSX.Element =>
		PendingList({ data, ...listProps })

const PendingComponentReader = <T, R>({ data, ...props }: AsyncComponentProps<T, R>) => {
	const readData = data.read()
	const Component = props.Component
	return <Component pending={false} {...props} {...readData} />
}

export const createPendingComponent =
	<T, R>(Component: PendingFC<T, R>) =>
	(props: AllSingleDataProps<T, R>): JSX.Element => {
		return (
			<LoadingBoundary fallback={<Component pending={true} {...props} />}>
				<PendingComponentReader Component={Component} {...props} />
			</LoadingBoundary>
		)
	}

type ArrayOrObject<A> = A extends Array<infer A> ? Array<A> : A

export interface ResolvedConfig<T, A> {
	config: T
	data: ArrayOrObject<A>
}

export const createDataWrappedConfig = <T, A>(
	dataWrapper: DataWrapper<ArrayOrObject<A>>,
	config: T
): DataWrapper<ResolvedConfig<T, A>> => dataWrapper.fork(list => ({ config: config, data: list }))

export const createDataWrappedConfigs = <T, A>(
	dataWrapper: DataWrapper<ArrayOrObject<A>>,
	configs: Array<T>
): Array<DataWrapper<ResolvedConfig<T, A>>> =>
	configs.map(config => dataWrapper.fork(list => ({ config: config, data: list })))
